import { useCallback, useEffect, useRef } from 'react';
import { useWebSocketEvent } from './use-websocket';
import { useVehiclesStore } from '@/store/vehicles-store';
import type { GpsPosition, Vehicle } from '@/lib/api';
import type { WsMessage } from '@/lib/ws';

// =============================================================================
// useVehicleSync — subscribe ke WS frame `type:"gps"` dan update vehicles
// store secara real-time. Backend kirim frame FLAT (lat/lng/speed di root,
// bukan nested di .gps), jadi kita reshape jadi { id, imei, nopol, gps: {...} }
// sebelum upsert.
//
// PERFORMANCE: 3000 vehicle × multi-frame/sec = re-render storm. Solusinya:
// buffer frame di ref, flush sekali per FLUSH_INTERVAL_MS dengan satu
// batch setState. Map cuma re-render ~1 kali/detik walau ratusan frame masuk.
//
// Tradeoff: marker lag max 1 detik dari realtime. Acceptable utk monitoring
// fleet besar; user yang fokus ke 1 vehicle (lewat Follow) tetap dapat
// posisi tiap detik (cukup smooth karena interpolasi map flyTo).
//
// Mount sekali di komponen yang selalu aktif (mis. AppLayout) — independent
// dari halaman, jadi vehicle store tetap up-to-date saat user di /vehicles,
// /playback, dll (bukan cuma /monitoring).
// =============================================================================

const FLUSH_INTERVAL_MS = 1000;

// Field di root frame yang masuk ke `gps` nested object.
// `valid` + `attributes` WAJIB ikut: warning "GPS bermasalah" di infowindow
// dibaca dari valid===0 / attributes.clock_fixed|gps_stuck — tanpa di-patch
// dari WS, flag sehat (valid=1) dari processing tidak pernah sampai store dan
// warning menempel terus sampai reload (stale dari REST initial load).
const GPS_FIELDS: (keyof GpsPosition)[] = [
  'lat', 'lng', 'speed', 'angle', 'acc', 'fcut', 'charge',
  'batt_percent', 'gsm', 'gsm_percent', 'sat', 'odo', 'mileage', 'fuel',
  'address', 'poi', 'gf', 'status', 'park_info', 'alarm', 'rent',
  'activity_state', 'activity_start', 'activity_state_duration',
  'total_on', 'total_off', 'total_move', 'total_idle', 'total_park', 'total_odo',
  'daily_move_seconds', 'daily_idle_seconds', 'daily_park_seconds', 'daily_engine_seconds', 'daily_distance',
  'accon_info', 'accoff_info', 'tdate', 'sdate', 'gpsNoData',
  'valid', 'attributes',
];
const VEHICLE_FIELDS = ['id', 'vh_id', 'imei', 'nopol', 'real_name', 'object_type'] as const;

type VehiclePatch = Partial<Vehicle> & { id: number };

export function useVehicleSync() {
  // Buffer per-vehicle latest frame. Map untuk dedupe — kalau vehicle X
  // kirim 10 frame dalam 1 detik, cuma frame terakhir yang di-apply
  // (gps_position adalah snapshot, intermediate frame tidak meaningful).
  const buffer = useRef<Map<number, VehiclePatch>>(new Map());
  const timer = useRef<number | null>(null);

  const flush = useCallback(() => {
    const buf = buffer.current;
    if (buf.size === 0) return;
    const state = useVehiclesStore.getState();
    // ENRICH-ONLY: WS frame cuma boleh update vehicle yang SUDAH ada di store
    // (hasil initial load REST yang punya object_type + identitas lengkap).
    // Tanpa guard ini, frame WS (yang tidak bawa object_type) bikin entri baru
    // → objectTypeToShape(undefined)='generic' → default.png ABU-ABU, dan muncul
    // satu-satu seiring frame masuk sebelum REST selesai. Frame untuk vehicle
    // yang belum ter-load di-skip; REST sudah punya posisi terakhirnya dari DB,
    // dan vehicle benar-benar baru akan ke-pickup di refetch berikutnya (5 menit
    // / saat reconnect). Hasil: marker muncul SERENTAK dari REST, lalu WS
    // memperkaya yang sudah ada.
    const patches = Array.from(buf.values()).filter((p) => state.byId.has(p.id));
    buf.clear();
    if (patches.length === 0) return;
    // Single batched mutation: 1 setState untuk N vehicles → cuma 1 re-render
    // selector subscriber, supercluster rebuild 1x per detik bukan per-frame.
    state.upsertMany(patches as Parameters<typeof state.upsertMany>[0]);
  }, []);

  // Polling timer flush — jalan terus selama hook mount.
  useEffect(() => {
    timer.current = window.setInterval(flush, FLUSH_INTERVAL_MS);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
      flush(); // flush sisa buffer saat unmount.
    };
  }, [flush]);

  const handle = useCallback((msg: WsMessage) => {
    const m = msg as Record<string, unknown>;
    const id = (m.id as number) ?? (m.vh_id as number) ?? 0;
    if (!id || id <= 0) return;

    // Merge ke existing patch kalau ada (frame yg lebih baru menimpa lama).
    const existing = buffer.current.get(id) ?? { id };
    const patch: VehiclePatch = { ...existing, id };

    for (const k of VEHICLE_FIELDS) {
      const v = m[k];
      // Skip undefined DAN empty-string untuk field identitas. Backend
      // heartbeat WS frame kadang kirim {nopol:"", imei:""} → tanpa guard
      // ini, value existing dari REST initial load ke-overwrite kosong →
      // sort key berubah → card di list pindah posisi tiap heartbeat.
      if (v === undefined) continue;
      if (typeof v === 'string' && v === '') continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (patch as any)[k] = v;
    }
    const gpsPatch: Partial<GpsPosition> = { ...(existing.gps ?? {}) };
    let gpsHasField = false;
    for (const k of GPS_FIELDS) {
      if (m[k] !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (gpsPatch as any)[k] = m[k];
        gpsHasField = true;
      }
    }
    // Frame WS tidak punya field flatten `gpsNoData` (hanya REST) — turunkan
    // dari attributes: flag ada → 1; frame posisi baru TANPA flag = GPS sudah
    // fix lagi → 0 (badge "NO GPS"/chip No Data pulih realtime tanpa reload).
    const attrs = m.attributes as Record<string, unknown> | undefined;
    if (attrs !== undefined) {
      gpsPatch.gpsNoData = attrs && (attrs.gpsNoData === 1 || attrs.gpsNoData === true) ? 1 : 0;
      gpsHasField = true;
    }
    if (gpsHasField) patch.gps = gpsPatch as GpsPosition;

    buffer.current.set(id, patch);
  }, []);

  useWebSocketEvent('gps', handle);
}
